// ===========================================================================
// Age proof, stored and reused ON THIS DEVICE for as long as the server signed
// it (two hours by default). After a user verifies their age, the signed age
// token is kept in localStorage, so a Lumi Link opened in a new tab, or from
// another app, does not send somebody who verified ten minutes ago back
// through the selfie check. The token is server-signed (the band cannot be
// spoofed) and short-lived.
//
// History, because this has moved twice. It sat in localStorage for a DAY,
// which made one selfie cover whoever used the browser next. It then moved to
// sessionStorage, per tab, for the reveal work. On 2026-09-12 the operator
// decided a new tab is not a different person, while a different device may
// be, and the two-hour life is the guard on a shared machine. So: per device,
// for two hours, and cleared the moment the token has expired.
//
// verifyAge() is UI-agnostic: callers pass onVideo/onStatus callbacks so the chat
// screen and the Lumi Plus sign-up sidebar can each render it their own way.
// ASCII only.
// ===========================================================================

import { SERVER_HTTP } from './config.js';
import { cameraErrorMessage, lumaVariance, BLANK_VARIANCE, meanLuma, SCREEN_LIGHT_BELOW, SCREEN_LIGHT_SETTLE_MS } from './camera-errors.js';
// What a waiting person is told, and when. Its own module so it can be tested
// without dragging Vite-only config in behind it.
import { waitingLine } from './waiting-line.js';
import { getKeepsake, storeKeepsake } from './keepsake.js';
import { channels } from './socket-channels.js';

const AGE_KEY = 'lumi.age';

/**
 * How many refused photos come before a person looks. Must match
 * RETAKES_BEFORE_HUMAN in packages/server/src/age-funnel.js: the server keeps
 * the authoritative count (so a reload does not reset the loop), and this is
 * the client's backstop for when it cannot -- no fingerprint, a changed
 * address. Setting it lower here silently defeats the server's rule, which is
 * exactly what `refusals >= 1` used to do.
 */
export const RETAKES_BEFORE_HUMAN = 3;
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/** Decode the (unverified) payload of a signed age token to read its band + exp. */
export function decodeAgeToken(token) {
  try {
    const dot = token.indexOf('.');
    if (dot <= 0) return null;
    let b64 = token.slice(0, dot).replace(/-/g, '+').replace(/_/g, '/');
    b64 += '='.repeat((4 - (b64.length % 4)) % 4); // restore base64url padding
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch {
    return null;
  }
}


/** The stored age proof if present and unexpired, else null (and it is purged). */
export function getStoredAge(nowMs = Date.now()) {
  try {
    // A tab that verified under the previous build kept its proof per tab.
    // Honour it once, by moving it to where proofs now live.
    const tabOnly = sessionStorage.getItem(AGE_KEY);
    if (tabOnly && !localStorage.getItem(AGE_KEY)) localStorage.setItem(AGE_KEY, tabOnly);
    sessionStorage.removeItem(AGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    const raw = JSON.parse(localStorage.getItem(AGE_KEY) || 'null');
    if (!raw || !raw.token || !raw.exp) return null;
    // Expiry is the server's, decoded from the token; an old proof is never
    // presented and never left behind for the next person.
    if (raw.exp * 1000 <= nowMs) {
      localStorage.removeItem(AGE_KEY);
      return null;
    }
    return raw; // { token, exp, band }
  } catch {
    return null;
  }
}

/** Persist an age token, keyed by the exp + band decoded from it. */
export function storeAge(token) {
  const p = decodeAgeToken(token);
  if (!p || !p.exp) return;
  try {
    localStorage.setItem(AGE_KEY, JSON.stringify({ token, exp: p.exp, band: p.band || null }));
  } catch {
    /* storage full / disabled -- non-fatal */
  }
}

export function clearAge() {
  try {
    sessionStorage.removeItem(AGE_KEY);
    localStorage.removeItem(AGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Open the front camera, stepping DOWN through constraints rather than giving
 * up on the first refusal. A 4K hint pinned to the front camera is the ideal
 * and the last two rungs are what a stubborn driver will actually grant; the
 * old single attempt turned a fussy device into "allow the camera".
 * A denied permission stops the ladder at once -- retrying cannot help, and
 * each attempt is another prompt the browser will not show.
 */
/**
 * The selfie camera, opened for one burst and given back straight after it.
 *
 * For a few hours on 2026-09-13 the stream was held across retakes, to save a
 * warm-up and, on browsers that grant the camera for one use only, a second
 * permission prompt. The operator watched what that did to real people: "Try
 * again" started the next burst half a second later with the camera already
 * running, so somebody still reading why the last photo failed was
 * photographed before they were ready. Opening the camera again is the pause
 * between one photo and the next, and where the browser asks again, that
 * question is the moment to get ready. The operator asked for it back.
 *
 * `openCamera` is only what is open right now, so a cancel, or the flow's own
 * finally, can still give it back if a burst never reached its own.
 */
let openCamera = null;

/**
 * Give a camera back. Safe to call twice, and safe to call when none is open.
 *
 * Given a stream, it stops THAT stream and forgets it only if it is still the
 * one open. A burst that finishes late, after its flow was cancelled and a new
 * flow has already opened the camera, must not stop the new flow's stream.
 */
export function releaseSelfieCamera(stream = openCamera) {
  if (stream) {
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      /* already gone */
    }
  }
  if (stream === openCamera) openCamera = null;
}

/**
 * How long the person has, once the camera is showing them, before the burst
 * starts. It was half a second, measured from when the camera was ASKED for,
 * so on a slow camera the burst could begin before the preview did. Now it is
 * counted from the first real frame, and long enough to look up and settle.
 */
export const READY_MS = 2000;

async function openSelfieCamera() {
  // Never reused: see openCamera. Anything still open is let go first so a
  // device that only allows one capture at a time can hand it back.
  releaseSelfieCamera();
  // The camera's best, which is what a full frame means.
  //
  // STANDING RULE (the operator, 2026-09-18, after this was lowered to 1080p
  // and put back the same day): the picture kept for review is the camera's
  // full resolution. Not a crop, not a reduced copy, and not a smaller
  // negotiation. Speed is bought by sending FEWER frames, never smaller ones.
  const res = { width: { ideal: 3840 }, height: { ideal: 2160 } };
  const ladder = [
    { video: { facingMode: { exact: 'user' }, ...res } },
    { video: { facingMode: 'user', ...res } },
    { video: { facingMode: 'user' } },
    { video: true },
  ];
  let lastErr = null;
  for (const constraints of ladder) {
    try {
      openCamera = await navigator.mediaDevices.getUserMedia(constraints);
      return openCamera;
    } catch (err) {
      lastErr = err;
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') break;
    }
  }
  throw lastErr || new Error('camera_unavailable');
}

/** Make an Error the caller can offer "Try again" for (vs a hard, terminal failure). */
const retryErr = (msg, extra = {}) => Object.assign(new Error(msg), { retry: true, ...extra });

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result || '').split(',')[1] || '');
    r.onerror = () => resolve('');
    r.readAsDataURL(blob);
  });
}

/**
 * How the burst is encoded, and how many frames it is.
 *
 * STANDING RULE (the operator, 2026-09-18): the picture kept for review is the
 * frame the decision was made from, whole and at the size it was captured.
 * Not a crop of it, not a smaller copy of it, and not a different frame taken
 * at another moment.
 *
 * That used to be two different pictures. Six frames went up at 1280px for the
 * models, and one full-resolution frame was taken halfway through the burst
 * for the operator: so the photo in the queue was never the photo that decided
 * anything, and when its encode timed out the operator got a 1280px frame
 * instead. Now there is one kind of frame, every one of them whole, and the
 * server keeps whichever one the model read (server.js).
 *
 * Three of them. A person has already granted the camera and watched
 * themselves settle for READY_MS, so three around a head turn is enough to
 * catch motion and give the model a middle reading; six was paying for the
 * same face six times.
 */
const BURST_FRAMES = 3;
const BURST_GAP_MS = 550;

/**
 * Send one frame the moment it is taken, while the next is still being
 * captured.
 *
 * Two things this buys, neither of which touches the picture (the operator,
 * 2026-09-18: "send as captured"). The upload overlaps the capture instead of
 * following it, so a burst that took a second and a half has already sent most
 * of itself by the time the last frame exists. And a frame goes as its own
 * bytes rather than base64 inside a JSON body, which is a third less to send
 * for exactly the same picture.
 *
 * Never awaited by the capture, and a failure is not an error: the frame that
 * did not arrive travels in the check itself, so a dropped upload costs one
 * frame's head start and nothing else.
 */
function sendFrame(uploadId, index, blob) {
  return fetch(`${SERVER_HTTP}/api/age/frame?upload=${encodeURIComponent(uploadId)}&i=${index}`, {
    method: 'POST',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
    credentials: 'omit',
  })
    .then((r) => r.status === 204)
    .catch(() => false);
}

/** An id for one burst. Unguessable, and the server binds it to this address. */
function newUploadId() {
  const rnd = globalThis.crypto?.randomUUID?.();
  if (rnd) return rnd.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}
const CAPTURE_QUALITY = 0.92;
/**
 * How long the browser waits for the server's answer before giving up.
 *
 * This was 60 seconds, chosen when the slowest real check took 55. With the
 * review frame at the camera's own size the upload is several megabytes, and a
 * phone on a weak uplink takes far longer: in one morning six checks were
 * abandoned by the browser and two timed out at the door, while the server
 * finished every one of them and approved most. The person was told nothing
 * and sat there (the operator, 2026-09-16: "the age check goes through, but
 * the page never refreshes").
 *
 * So the browser now waits longer than any upload observed (the slowest was
 * 249 seconds), and says what is happening while it waits. Giving up early
 * throws away a decision that has already been made; waiting costs a person
 * who is being told what is going on nothing.
 */
export const UPLOAD_BUDGET_MS = 300_000;

/**
 * Capture a short BURST of frames (active-liveness): a held static photo yields nearly
 * identical frames, a real person responding to the move/blink prompt yields motion.
 * Returns { blobs, maxVariance } -- the variance so the caller can tell a real
 * photo from a stream that never produced one. onTick(i, n) drives the progress copy.
 */
async function captureBurst(stream, n, gapMs, onTick, readyMs = 0, isCancelled = () => false, onLight = null, onFrame = null) {
  const v = document.createElement('video');
  v.srcObject = stream;
  v.muted = true;
  v.playsInline = true;
  await v.play().catch(() => {});
  // Wait (up to ~2s) for the first real frame; a black frame is worthless.
  for (let i = 0; i < 40 && !v.videoWidth; i += 1) await delay(50);
  // Too dark to read a face? Light it from the screen. The person is looking
  // at the phone, and the page around the camera circle was black: "too dark"
  // was the most common refusal. Only when the first picture is dim, never by
  // default, and the caller turns it off once the photos are taken (the
  // operator, 2026-09-15). A picture that cannot be measured leaves the screen
  // as it is.
  let lit = false;
  if (onLight && v.videoWidth) {
    try {
      const c = document.createElement('canvas');
      c.width = 32;
      c.height = 32;
      const cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(v, 0, 0, 32, 32);
      if (meanLuma(cx.getImageData(0, 0, 32, 32).data) < SCREEN_LIGHT_BELOW) {
        lit = true;
        onLight(true);
      }
    } catch {
      /* cannot measure: leave the screen as it is */
    }
  }
  // Then the time to get ready, counted from when the person can SEE
  // themselves. See READY_MS. With the screen lit, a moment more, so the
  // camera's exposure settles on the new light before a frame is kept.
  if (readyMs > 0) await delay(readyMs + (lit ? SCREEN_LIGHT_SETTLE_MS : 0));
  // Cancelled while getting ready: stop before a single frame is taken, so
  // the camera goes off with the screen rather than seconds after it.
  if (isCancelled()) throw new Error('cancelled');
  // Every frame whole, at the size the camera is giving us. The models scale
  // what they need down themselves; what goes up is what a person will judge.
  const w = v.videoWidth || 1280;
  const h = v.videoHeight || 720;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  // A 32x32 copy of each frame, to judge whether anything was drawn at all.
  const probe = document.createElement('canvas');
  probe.width = 32;
  probe.height = 32;
  const pctx = probe.getContext('2d', { willReadFrequently: true });
  let maxVariance = 0;
  const blobs = [];
  for (let i = 0; i < n; i += 1) {
    ctx.drawImage(v, 0, 0, w, h);
    try {
      pctx.drawImage(v, 0, 0, 32, 32);
      maxVariance = Math.max(maxVariance, lumaVariance(pctx.getImageData(0, 0, 32, 32).data));
    } catch {
      maxVariance = Infinity; // never let the probe itself refuse a real photo
    }
    // toBlob can hang on some mobile browsers -- race it against a timeout.
     
    const blob = await Promise.race([
      new Promise((res) => canvas.toBlob(res, 'image/jpeg', CAPTURE_QUALITY)),
      // A whole 1080p frame takes longer to encode on a slow phone than the
      // reduced one did, and a frame that never arrives is one the burst has
      // to do without.
      delay(6000).then(() => null),
    ]);
    if (blob) {
      blobs.push(blob);
      // Away it goes, while the person is still being photographed.
      onFrame?.(blobs.length - 1, blob);
    }
    onTick?.(i + 1, n);
     
    if (i < n - 1) await delay(gapMs);
  }
  return { blobs, maxVariance };
}

/**
 * One status fetch, bounded: a hung connection must not stall the wait.
 *
 * The fallback, for the moments no socket carries the channel (the server's
 * visitor cap, or an outage): the check is watched on the page's socket
 * otherwise (awaitDecision). Every ask sends the keepsake stored at that
 * moment, as the socket offered it at the door.
 *
 * @param {string} checkId
 * @param {boolean} [hold]  ask the server to hold the request open until the
 *   check changes (long poll). The decision then arrives as fast as the
 *   response can be written, instead of on the next tick of a backoff.
 */
async function fetchAgeStatus(checkId, hold = false) {
  const controller = new AbortController();
  // Longer than the server's own hold, so the abort is a real failure rather
  // than the normal end of a held request.
  const timer = setTimeout(() => controller.abort(), hold ? 35000 : 10000);
  try {
    const url = `${SERVER_HTTP}/api/age/status?checkId=${encodeURIComponent(checkId)}${hold ? '&wait=1' : ''}`;
    // The keepsake goes along, so the proof for an approved check is bound to
    // this device the moment it is handed over.
    const ks = getKeepsake()?.key;
    const res = await fetch(url, { signal: controller.signal, ...(ks ? { headers: { 'X-Lumi-Keepsake': ks } } : {}) });
    // Throttled is a failed request, not an answer, so the loop backs off
    // rather than asking again at once.
    if (res.status === 429) return null;
    return await res.json();
  } catch {
    return null; // offline, or slow: the next poll tries again
  } finally {
    clearTimeout(timer);
  }
}

/**
 * How many times a waiting tab asks again about an approved check whose proof
 * another tab already redeemed, before saying the check could not be completed
 * here. The first tab stores its keepsake within moments and the next poll
 * carries it; the waits add up to under eight seconds.
 */
const REDEEMED_WAITS = 4;

/** How often a wait that nothing is arriving on asks whether it was cancelled. Local; no request. */
const CANCEL_CHECK_MS = 1000;
/** How long the first answer about a check may take before it counts as offline. */
const FIRST_ANSWER_MS = 10_000;

/**
 * What the server says about a check now: the channel answers at once, and
 * while no socket carries it the route is asked once. Null when neither
 * answers in time, which the caller treats as offline.
 */
function currentStatus(checkId) {
  return new Promise((resolve) => {
    let done = false;
    let off = () => {};
    const finish = (data) => {
      if (done) return;
      done = true;
      clearTimeout(deadline);
      off();
      resolve(data);
    };
    const deadline = setTimeout(() => finish(null), FIRST_ANSWER_MS);
    off = channels.subscribe(`age:${checkId}`, finish);
    if (!channels.isBound()) fetchAgeStatus(checkId, false).then(finish, () => finish(null));
  });
}

/**
 * The proof, once a person has decided.
 *
 * Watched on the page's socket (the channel 'age:<checkId>', see
 * server/src/visitors.js), which answers at once and again the moment the
 * check changes: a question from the reviewer, a live-check offer, the
 * decision. THERE IS NO TIMER ON THE HAPPY PATH: a decision reaches this page
 * as fast as the frame can be written. It used to be a held request, asked
 * again the moment it came back; that is now the fallback, made only while no
 * socket carries the channel (the server's visitor cap, or an outage), and
 * even then it waits only after a failure, so a broken connection never
 * becomes a hot loop.
 *
 * No deadline. A check waits for a person for as long as that takes. The old
 * four-minute give-up threw the person out of the wait with a button that
 * only put them back into it.
 */
function awaitDecision(checkId, isCancelled, onMessages, onLive, onDecided) {
  return new Promise((resolve, reject) => {
    let done = false;
    let redeemedWaits = 0;
    let again = null;
    let offChannel = () => {};
    let offAsking = () => {};
    const finish = (settle) => {
      if (done) return;
      done = true;
      clearTimeout(again);
      clearInterval(cancelCheck);
      offChannel();
      offAsking();
      settle();
    };
    // Cancelling (the Back button) is asked about on a local tick, so a wait
    // nothing is arriving on still lets the channel go.
    const cancelCheck = setInterval(() => {
      if (isCancelled()) finish(() => reject(new Error('cancelled')));
    }, CANCEL_CHECK_MS);
    const handle = (data) => {
      if (done) return;
      if (isCancelled()) return finish(() => reject(new Error('cancelled')));
      if (data == null) return undefined;
      // An approval whose proof another tab of this browser redeemed, with
      // nothing stored by that tab yet, is looked at again shortly: the first
      // tab stores its proof within moments, and what is stored is re-read
      // here, not asked of the server. Briefly (REDEEMED_WAITS).
      const waitForOtherTab = data.status === 'approved' && !data.ageToken && data.redeemed && !getStoredAge() && redeemedWaits < REDEEMED_WAITS;
      if (waitForOtherTab) {
        redeemedWaits += 1;
        clearTimeout(again);
        again = setTimeout(() => handle(data), 1500);
        return undefined;
      }
      // Decided. The page is told before anything else happens, so a tab
       // nobody is looking at can say so (app.js, noteDecided).
      if (data.status === 'approved' && data.ageToken) {
        onDecided?.();
        return finish(() => resolve(data.ageToken));
      }
      if (data.status === 'approved') {
        // Approved with no proof: another tab of this browser redeemed it and
        // stores the proof and the keepsake it is bound to, or another device
        // did. What another tab stored is this browser's, so carry on with it.
        const stored = getStoredAge();
        if (stored) return finish(() => resolve(stored.token));
        // Nothing stored after the waits above: a device that never holds the
        // proof is told the check could not be completed here, which is true,
        // and never that it was replaced.
        return finish(() => reject(retryErr('Age check could not be completed. Please try again.', { closed: true })));
      }
      if (data.status === 'rejected') {
        // Told the same way an approval is: a person left on a screen that
        // will never change again is the worse of the two silences.
        onDecided?.();
        return finish(() => reject(Object.assign(new Error('We could not confirm you are old enough to use Lumi.'), { rejected: true })));
      }
      if (data.status === 'undecided') {
        // A check closed before anyone looked (there is no clock on them any
        // more, but older ones exist). Not a refusal: a fresh photo usually
        // decides on its own, so the button says so.
        return finish(() => reject(retryErr('That check was closed before anyone could look at it. Please take a fresh photo.', { undecided: true })));
      }
      if (data.error === 'not_found' || (data.status && data.status !== 'pending')) {
        // Superseded by a newer check from this device, or gone: this one is
        // over, and waiting on it would be waiting for an answer that never
        // comes.
        return finish(() => reject(retryErr('That check was replaced by a newer one. Please take a fresh photo.', { closed: true })));
      }
      // The review thread: a moderator may ask the waiting user a question here.
      if (Array.isArray(data.messages) && data.messages.length) onMessages?.(data.messages);
      // A live-check request: the moderator wants a brief live camera view.
      if (data.liveOffer) onLive?.({ offer: data.liveOffer, ice: data.ice || null });
      return undefined;
    };
    offChannel = channels.subscribe(`age:${checkId}`, handle);
    offAsking = channels.askWhileUnbound(() => fetchAgeStatus(checkId, true), handle);
  });
}

// The check currently in the human-review queue, remembered across page loads
// and retries: a phone that locks its screen (suspending the poll) must be able
// to pick up the decision later instead of submitting a fresh selfie -- and
// looping through the queue again -- for a check that was already approved.
const PENDING_KEY = 'lumi.ageCheckId';
function getPendingCheckId() {
  try {
    return localStorage.getItem(PENDING_KEY) || '';
  } catch {
    return '';
  }
}
function setPendingCheckId(id) {
  try {
    if (id) localStorage.setItem(PENDING_KEY, id);
    else localStorage.removeItem(PENDING_KEY);
  } catch {
    /* storage unavailable -- resume just will not work */
  }
}

/**
 * Watch a retake while the retry screen is up. A person can still decide it:
 * the operator setting a band on the card approves the check (2026-09-15), and
 * the person waiting should be let in then, not asked for another photo the
 * model cannot read. Resolves with the proof, stored, when that happens;
 * nothing else ends it. stop() lets the channel go.
 *
 * @param {string} checkId
 * @returns {{ promise: Promise<string>, stop: () => void }}
 */
export function watchForApproval(checkId) {
  let off = () => {};
  let settled = false;
  const promise = new Promise((resolve) => {
    off = channels.subscribe(`age:${checkId}`, (data) => {
      if (settled || !data || data.status !== 'approved' || !data.ageToken) return;
      settled = true;
      setPendingCheckId('');
      storeAge(data.ageToken);
      resolve(data.ageToken);
    });
  });
  return { promise, stop: () => off() };
}

/** Send the WebRTC answer for a live check; resolves to true on success. */
async function sendLiveAnswer(checkId, answer) {
  try {
    const res = await fetch(`${SERVER_HTTP}/api/age/live-answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkId, answer }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Send a reply into the review thread; resolves to the updated thread or null. */
async function sendAgeReply(checkId, text) {
  try {
    const res = await fetch(`${SERVER_HTTP}/api/age/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkId, text }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data.messages) ? data.messages : null;
  } catch {
    return null;
  }
}

/**
 * Run the self-hosted selfie age check and return a signed age token (also stored).
 * @param {number} birthYear
 * @param {string} fingerprint
 * @param {{ onVideo?: (s: MediaStream) => void, onStatus?: (title: string, sub?: string) => void, isCancelled?: () => boolean }} cb
 */
export async function verifyAge(birthYear, fingerprint, cb = {}) {
  const isCancelled = cb.isCancelled || (() => false);

  // Resume: a prior session may have left a check in the review queue (screen
  // locked, tab closed, poll interrupted). Pick up its outcome before taking a
  // new selfie -- a decision made while we were away applies immediately.
  const pendingId = getPendingCheckId();
  if (pendingId) {
    // From the page's socket, which offered the keepsake at the door, so an
    // approval that came while they were away is bound to this device when
    // it is handed over. Null is offline: fall through to a fresh check.
    const st = await currentStatus(pendingId);
    if (st?.status === 'approved' && st.ageToken) {
      setPendingCheckId('');
      storeAge(st.ageToken);
      return st.ageToken;
    }
    // Approved, and redeemed by another tab of this browser while this one was
    // away: carry on with the proof that tab stored rather than a fresh photo.
    if (st?.status === 'approved' && !st.ageToken) {
      const stored = getStoredAge();
      if (stored) {
        setPendingCheckId('');
        return stored.token;
      }
    }
    if (st?.status === 'rejected') {
      // Decided while they were away. Saying nothing and opening the camera
      // again was the old behaviour, which read as a glitch and cost the
      // person another selfie for the same answer.
      setPendingCheckId('');
      throw Object.assign(new Error('We could not confirm you are old enough to use Lumi.'), { rejected: true });
    }
    if (st?.status === 'pending') {
      cb.onStatus?.(
        "You're in the review queue",
        `Picking up where you left off. ${waitingLine(st)}`,
      );
      cb.onChatReady?.((text) => sendAgeReply(pendingId, text));
      try {
        const token = await awaitDecision(
          pendingId,
          isCancelled,
          (msgs) => cb.onMessages?.(msgs),
          (live) => cb.onLiveCheck?.({ ...live, sendAnswer: (answer) => sendLiveAnswer(pendingId, answer) }),
          () => cb.onDecided?.(),
        );
        setPendingCheckId('');
        storeAge(token);
        return token;
      } catch (err) {
        // ONLY a real rejection forgets the check. A cancel or interruption
        // keeps it, so a decision made after the user walks away still applies
        // the moment they come back -- otherwise an approval decided during
        // their absence is lost and they loop through the queue again.
        if (err?.rejected || err?.undecided || err?.closed) setPendingCheckId(''); // a closed case is not resumed
        throw err;
      }
    }
    if (st?.status === 'undecided') {
      // Say why they are being asked for another photo, rather than silently
      // dropping them back at the camera as though nothing had happened.
      cb.onStatus?.(
        'That check was closed',
        'It was closed before anyone could look at it. Taking a fresh photo now. This usually decides on its own.',
      );
    }
    if (st && st.status) setPendingCheckId(''); // resolved some other way -- start fresh
  }

  let selfie;
  try {
    // Front camera first, at the best size the device will give, stepping down
    // rather than failing on the first refusal. See openSelfieCamera.
    selfie = await openSelfieCamera();
  } catch (err) {
    // Browsers never re-prompt once a camera is blocked, so a message with no
    // instructions is a locked door -- and "allow the camera" is the wrong
    // instruction when the camera is merely busy. Say which it was.
    throw retryErr(cameraErrorMessage(err));
  }
  if (isCancelled()) {
    releaseSelfieCamera(selfie);
    throw new Error('cancelled');
  }
  cb.onVideo?.(selfie);
  // Active liveness: ask for a small movement and grab a short burst so a static photo
  // (near-identical frames) cannot pass, while a real person (motion) does.
  cb.onStatus?.('Look at the camera', 'Slowly turn your head a little, or blink. That is how we confirm it is really you.');
  let blobs;
  let maxVariance = 0;
  const uploadId = newUploadId();
  /** @type {Map<number, Promise<boolean>>} index -> did it land */
  const sending = new Map();
  // The check needs MOTION (a held photo yields near-identical frames), so the
  // copy has to ask for exactly one thing -- the old "Hold still, keep moving
  // a little" asked for both at once.
  //
  // The camera is given back the moment the burst is taken, whatever happened
  // during it, so a retake opens it afresh. See openCamera.
  try {
    ({ blobs, maxVariance } = await captureBurst(
      selfie,
      BURST_FRAMES,
      // Wider than the 280ms six frames were taken at. Liveness is the motion
      // BETWEEN consecutive frames, so half a second apart gives a head turn
      // time to show; three frames this far apart still span less of the
      // person's time than six did.
      BURST_GAP_MS,
      (i, n) => cb.onStatus?.('Keep turning your head slowly', `Capturing (${i}/${n})...`),
      READY_MS,
      isCancelled,
      (on) => cb.onLight?.(on),
      (i, blob) => sending.set(i, sendFrame(uploadId, i, blob)),
    ));
  } finally {
    // The screen light goes back down the moment the photos are taken, or the
    // burst ended some other way.
    cb.onLight?.(false);
    releaseSelfieCamera(selfie);
    // A stopped stream left in the preview shows a frozen face through the
    // whole check and any wait for a person, which reads as the camera still
    // being on. It is off, so the preview says so.
    cb.onVideo?.(null);
  }
  // Drop black/empty frames (camera not ready, lens covered).
  let valid = (blobs || []).filter((b) => b && b.size >= 1500);
  if (!valid.length) {
    throw retryErr('We could not capture a clear photo. Make sure your camera is uncovered and well lit, then try again.');
  }
  // The camera granted a stream but sent nothing: every frame one flat colour.
  // Caught here rather than sent, because the estimator can only answer
  // "no face", which reads as "your face was rejected" and is not what
  // happened. Common when another tab or app holds the camera.
  if (maxVariance < BLANK_VARIANCE) {
    throw retryErr(
      'Your camera did not send a picture. Every frame was blank. Close anything else that may be using it (another Lumi tab, a video call), check the lens is uncovered, then try again.',
    );
  }
  cb.onStatus?.('Checking...', 'This only takes a moment.');
  // A backstop for a camera that produces something enormous. It drops a
  // FRAME, never the size of one: a frame the operator judges by is the
  // camera's own, and re-encoding it smaller is the thing this is not allowed
  // to do (the operator, 2026-09-18). Two frames is the floor, because a
  // single picture is not a burst and the server will not admit one.
  //
  // nginx accepts 32MB on this route and the server reads up to 32MB, so the
  // budget is the smaller of what they take, with room for the rest of the
  // JSON. A 4K burst of three runs six to nine megabytes and never reaches it.
  const totalB64 = (list) => (list.reduce((n, b) => n + b.size, 0) * 4) / 3;
  const B64_BUDGET = 28_000_000;
  while (valid.length > 2 && totalB64(valid) > B64_BUDGET) valid = valid.slice(0, -1);
  // Wait for the frames already on their way, then carry only the ones that
  // did not make it. A frame that landed is on the server; sending it again
  // would upload the same picture twice.
  const landed = await Promise.all(valid.map((_, i) => sending.get(i) ?? Promise.resolve(false)));
  const missed = valid.filter((_, i) => !landed[i]);
  const frames = (await Promise.all(missed.map(blobToBase64))).filter(Boolean);
  // Cancel pressed during the capture used to send the frames anyway, create
  // a check, and remember it for a review the person believed they had
  // called off.
  if (isCancelled()) throw new Error('cancelled');

  // How long to wait for the server's answer.
  //
  // This was 20 seconds, and 22% of real age checks took longer than that --
  // 33 of 148 in one day, with a mean of 15.3s. The nginx log shows them as
  // client-aborted: the browser gave up, showed "the check is taking too
  // long", and threw away a check the server went on to finish and often
  // APPROVE. The person was told their connection had failed while the server
  // was letting them in.
  //
  // The time is not the estimator, which answers a frame in about 0.4s. It is
  // the upload: a burst of frames plus a full-resolution review frame, off a
  // phone, on whatever signal the phone has. So the budget has to cover a slow
  // uplink, and 60s clears every real check observed (the slowest was 55s).
  // A person is told what is happening at 10 and 25 seconds rather than being
  // left watching a spinner.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_BUDGET_MS);
  const slowNotes = [
    setTimeout(() => cb.onStatus?.('Still checking...', 'Sending your photos. This can take a moment on a phone.'), 10000),
    setTimeout(() => cb.onStatus?.('Still checking...', 'A slow connection makes this take longer. Please keep this page open.'), 25000),
    setTimeout(() => cb.onStatus?.('Still sending your photos', 'Your photos are still going up. Keep this page open and you will not have to take them again.'), 60000),
    setTimeout(() => cb.onStatus?.('Still sending your photos', 'This is a slow connection, so it is taking a few minutes. Keep this page open.'), 120000),
  ];
  let res;
  try {
    res = await fetch(`${SERVER_HTTP}/api/age/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        birthYear,
        fingerprint,
        // The burst that went up frame by frame while it was being taken.
        uploadId,
        // And whatever did not get there, so a failed upload costs a frame
        // rather than the check.
        frames,
        escalate: !!cb.escalate,
        // So the reviewer sees WHO they are deciding about.
        handle: cb.handle || undefined,
        accountToken: cb.accountToken || undefined,
        // The keepsake this browser already holds, so what it did at the check
        // is remembered when its address changes. The key only, as to /api/session.
        keepsake: getKeepsake()?.key || undefined,
      }),
      signal: controller.signal,
    });
  } catch {
    throw retryErr('The check is taking too long. Please check your connection and try again.');
  } finally {
    for (const t of slowNotes) clearTimeout(t);
    clearTimeout(timer);
  }
  if (res.status === 403) {
    const b = await res.json().catch(() => ({}));
    if (b.error === 'under_age') throw new Error(b.message || 'You must be at least 13 to use Lumi.');
    throw new Error('Access to Lumi has been restricted from this device.');
  }
  if (res.status === 400) {
    // image_rejected (nsfw) / liveness_failed / bad image -- a retake can fix it.
    const b = await res.json().catch(() => ({}));
    throw retryErr(b.message || 'That photo was not accepted. Please retake it facing the camera in good light.', { refusal: true, checkId: b.checkId || null });
  }
  if (res.status === 429) {
    // The server says how long; "could not be completed" hid that and the
    // Try again button walked straight back into the limit.
    const b = await res.json().catch(() => ({}));
    throw retryErr(b.message || 'Too many attempts. Please wait a few minutes and try again.');
  }
  if (!res.ok) throw retryErr('Age check could not be completed. Please try again.');
  const data = await res.json();
  if (data.status === 'approved') {
    setPendingCheckId(''); // any older check is moot now
    storeAge(data.ageToken);
    return data.ageToken;
  }
  if (data.status === 'retake') {
    setPendingCheckId('');
    // The model could not read a clear face -- let the user immediately retake.
    // The reason rides along so the retry screen can offer the birth year when
    // the refusal was about the year.
    throw retryErr(data.message || 'We could not get a clear read of your face. Please retake the photo in good light, facing the camera.', { refusal: true, reason: data.reason || null, checkId: data.checkId || null });
  }
  if (data.status === 'pending') {
    setPendingCheckId(data.checkId); // survive interruption; see the resume path above
    cb.onStatus?.(
      "You're in the review queue",
      // What the SERVER said about when a person is around, not a guess. data
      // carries reviewing/reviewNext from /api/age/verify's pending answer.
      waitingLine(data),
    );
    // While waiting, a moderator may ask a question; the UI gets a sender for
    // replies and a stream of thread updates from the status poll. A live-check
    // request (SDP offer + ICE) is delivered the same way.
    cb.onChatReady?.((text) => sendAgeReply(data.checkId, text));
    try {
      const token = await awaitDecision(
        data.checkId,
        isCancelled,
        (msgs) => cb.onMessages?.(msgs),
        (live) => cb.onLiveCheck?.({ ...live, sendAnswer: (answer) => sendLiveAnswer(data.checkId, answer) }),
        () => cb.onDecided?.(),
      );
      setPendingCheckId('');
      storeAge(token);
      return token;
    } catch (err) {
      if (err?.rejected || err?.undecided || err?.closed) setPendingCheckId(''); // a closed case is not resumed
      throw err;
    }
  }
  throw retryErr('Age check could not be completed. Please try again.');
}

/**
 * Return a valid age token without re-prompting when possible:
 *   1. a stored, unexpired token;
 *   2. self-declared mode hands one back from /api/session (no selfie);
 *   3. otherwise run the selfie verification (verified mode).
 * Returns the token (any band -- the caller checks), or throws on under-age/cancel.
 */
export async function ensureAgeToken(birthYear, fingerprint, cb = {}) {
  const stored = getStoredAge();
  if (stored) {
    const bound = await bindToThisDevice(stored.token, birthYear, fingerprint);
    if (bound) return bound;
    // Bound to a keepsake this browser no longer holds (forgotten, or replaced
    // after a band change): nothing will accept it here, so it is dropped and
    // this device goes on exactly as it would with no proof stored.
    clearAge();
  }

  try {
    const r = await fetch(`${SERVER_HTTP}/api/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The keepsake goes along, and a new one is kept: the proof this hands
      // back is bound to it, and register asks for its key.
      body: JSON.stringify({ birthYear, fingerprint, keepsake: getKeepsake()?.key }),
    });
    if (r.status === 403) {
      const b = await r.json().catch(() => ({}));
      if (b.error === 'under_age') throw new Error(b.message || 'You must be at least 13 to use Lumi.');
    } else if (r.ok) {
      const d = await r.json();
      if (d.keepsake) storeKeepsake(d.keepsake);
      if (d.ageToken) {
        storeAge(d.ageToken);
        return d.ageToken;
      }
    }
    // 401 verification_required -> fall through to the selfie verification.
  } catch (e) {
    if (e && /least 13/.test(String(e.message))) throw e;
  }

  const verified = await verifyAge(birthYear, fingerprint, cb);
  return (await bindToThisDevice(verified, birthYear, fingerprint)) || verified;
}

/**
 * The proof, bound to this device.
 *
 * A proof straight from the selfie check names no device, and the server lets
 * only the first device that redeems it at /api/session use it; register and
 * claim refuse it until then. The chat flow redeems it at once. The account
 * forms go straight to register, so they redeem it here. The bound proof is
 * returned for that one use and NOT stored, so the proof kept on this device
 * still lapses two hours after the check, as decided.
 *
 * A proof already bound counts only with the keepsake this browser holds;
 * bound to any other, it is null, for the caller to drop. A binding the server
 * refuses (a 429) hands back the proof as it was, which register redeems.
 */
async function bindToThisDevice(token, birthYear, fingerprint) {
  const claims = decodeAgeToken(token);
  if (!claims) return token;
  if (claims.ks) return claims.ks === getKeepsake()?.id ? token : null;
  try {
    const r = await fetch(`${SERVER_HTTP}/api/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ birthYear, fingerprint, ageToken: token, keepsake: getKeepsake()?.key }),
    });
    if (!r.ok) return token;
    const d = await r.json();
    if (d.keepsake) storeKeepsake(d.keepsake);
    return typeof d.ageToken === 'string' && d.ageToken ? d.ageToken : token;
  } catch {
    return token;
  }
}

export default { decodeAgeToken, getStoredAge, storeAge, clearAge, verifyAge, ensureAgeToken };
